import { useLayoutEffect, useRef, useState } from 'react'

interface ThumbStyle {
  left: number
  width: number
  ready: boolean
}

/**
 * Volgt het actieve element binnen een container en geeft de positie/breedte
 * terug voor een absoluut gepositioneerd "thumb"-vlak dat ernaartoe schuift.
 * De actieve knop wordt gevonden via het data-active attribuut.
 */
export function useSlidingThumb<T>(
  activeKey: T,
  activeSelector = '[data-active="true"]',
): {
  containerRef: React.RefObject<HTMLDivElement | null>
  thumb: ThumbStyle
} {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [thumb, setThumb] = useState<ThumbStyle>({ left: 0, width: 0, ready: false })

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeKey stuurt de hermeting aan; zonder die dep blijft de thumb op de oude knop staan
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    function measure() {
      const el = container?.querySelector<HTMLElement>(activeSelector)
      if (!el || !container) return
      setThumb({ left: el.offsetLeft, width: el.offsetWidth, ready: true })
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    for (const child of Array.from(container.children)) observer.observe(child)
    return () => observer.disconnect()
  }, [activeKey, activeSelector])

  return { containerRef, thumb }
}
